import moment from "moment";
import { ChipProps } from "@mui/material";
import { DocumentInfo } from "../../../Repositories/DocumentRepository";
import { getActivePrestationBrandConfig } from "../../../config/environment.config";

/**
 * Seuil « expire bientôt » des abonnements transport : même valeur que le
 * bandeau de DocumentSpace et que le cron d'alerte backend
 * (AlertExpiringTransportSubscriptionsCommand::EXPIRY_ALERT_DAYS).
 */
export const EXPIRY_ALERT_DAYS = 7;

export interface DocumentStatus {
    /** Clé stable : active, replaced, valid, invalidated, to_confirm, upcoming, expiring, expired. */
    key: string;
    label: string;
    color: ChipProps["color"];
    tooltip?: string;
}

const formatDay = (value: string): string => moment(value, "YYYY-MM-DD").format("DD/MM/YYYY");

/** Période de validité d'un abonnement transport : « du JJ/MM/AAAA au JJ/MM/AAAA ». */
export const transportPeriod = (doc: DocumentInfo): string | null =>
    doc.validFrom && doc.validUntil ? `du ${formatDay(doc.validFrom)} au ${formatDay(doc.validUntil)}` : null;

/**
 * Statut lisible d'un document, décliné par type (une seule colonne
 * « Statut » pour tous les types). null quand le type n'a pas de statut
 * métier : factures, récapitulatifs, attestation fiscale.
 */
export const statusOf = (doc: DocumentInfo): DocumentStatus | null => {
    switch (doc.type) {
        case "quote":
            if (doc.status === "replaced") {
                return {
                    key: "replaced",
                    label: "Remplacé",
                    color: "default",
                    tooltip: `Un devis plus récent a été émis pour ${getActivePrestationBrandConfig("", "ce ", "cette ")}`,
                };
            }
            return doc.status === "active"
                ? { key: "active", label: "En vigueur", color: "success", tooltip: `Devis applicable à ${getActivePrestationBrandConfig("", "ce ", "cette ")}` }
                : null;

        case "travel_attestation":
            if (doc.status === "invalidated") {
                return {
                    key: "invalidated",
                    label: "Invalidée",
                    color: "warning",
                    tooltip: doc.statusReason || "Une nouvelle attestation doit être signée",
                };
            }
            return doc.status === "valid"
                ? { key: "valid", label: "Valide", color: "success", tooltip: "Attestation signée, toujours applicable" }
                : null;

        case "deposit_attestation":
            if (doc.status === "superseded") {
                return {
                    key: "superseded",
                    label: "Remplacée",
                    color: "default",
                    tooltip: doc.statusReason || "Une attestation plus récente a été signée pour cet acompte",
                };
            }
            return doc.status === "valid"
                ? { key: "valid", label: "En vigueur", color: "success", tooltip: "Attestation signée, applicable à cet acompte" }
                : null;

        case "transport": {
            const period = transportPeriod(doc);
            const days = doc.expiresInDays;
            // Expiré testé EN PREMIER : expiresInDays est signé, un abonnement
            // périmé ne doit jamais passer pour un abonnement qui expire bientôt.
            if (doc.status === "expired" || (days != null && days < 0)) {
                return {
                    key: "expired",
                    label: "Expiré",
                    color: "error",
                    tooltip: doc.validUntil ? `Expiré le ${formatDay(doc.validUntil)}` : undefined,
                };
            }
            // Adresse modifiée : le prof doit confirmer que l'abonnement
            // couvre toujours le trajet, il ne vaut pas preuve d'ici là.
            if (doc.status === "to_confirm") {
                return {
                    key: "to_confirm",
                    label: "À confirmer",
                    color: "warning",
                    tooltip: doc.statusReason || "Confirmez que l'abonnement couvre toujours ce trajet",
                };
            }
            if (doc.validFrom && moment(doc.validFrom, "YYYY-MM-DD").isAfter(moment(), "day")) {
                return {
                    key: "upcoming",
                    label: "À venir",
                    color: "info",
                    tooltip: `Valide à partir du ${formatDay(doc.validFrom)}`,
                };
            }
            if (days != null && days <= EXPIRY_ALERT_DAYS) {
                return {
                    key: "expiring",
                    label: days === 0 ? "Expire aujourd'hui" : `Expire dans ${days} jour${days > 1 ? "s" : ""}`,
                    color: "warning",
                    tooltip: "Pensez à déposer le nouveau justificatif",
                };
            }
            return doc.status === "valid"
                ? { key: "valid", label: "Valide", color: "success", tooltip: period ? `Valide ${period}` : undefined }
                : null;
        }

        default:
            return null;
    }
};
